import { ChangeDetectionStrategy, Component, DestroyRef, effect, HostListener, inject, input, model, signal } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { HttpClient } from "@angular/common/http";
import { ResourcePipe } from "@app/kernel/tools/download-files/downloaded-files.config";
import { TranslateModule } from "@ngx-translate/core";
import { SvgIconComponent } from "@kernel/tools/svg-icon/components/svg-icon/svg-icon.component";
import { TPdfPreviewResolveUrlFn } from "../../interfaces/pdf-preview.interfaces";

/**
 * PDF preview (lightbox) component that renders PDFs using the browser's native viewer.
 *
 * Opened via {@link PdfPreviewService} which dynamically creates and attaches this component.
 *
 * Features:
 * - Full-screen overlay with embedded PDF via iframe
 * - Loading skeleton/spinner + error state with retry
 **/
@Component({
  selector: "sxw-pdf-preview",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./pdf-preview.component.html",
  styleUrls: ["./pdf-preview.component.scss"],
  providers: [ResourcePipe],
  imports: [TranslateModule, SvgIconComponent],
})
export class PdfPreviewComponent {
  /** Controls whether the preview overlay is visible. */
  readonly opened = model<boolean>(false);

  /** The file key or direct URL of the PDF to preview. */
  readonly file = input<string | null>(null);

  /** Extra arguments passed to ResourcePipe.transform. */
  readonly resourcePipeOptions = input<unknown[]>([]);

  /** Resolves a file key into a usable PDF URL. */
  readonly resolveUrl = input<TPdfPreviewResolveUrlFn>((key, defaultResolve) => defaultResolve(key));

  private readonly resourcePipe = inject(ResourcePipe);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  /** Whether the PDF is currently loading. */
  readonly isLoading = signal<boolean>(false);

  /** Whether the last load attempt failed. */
  readonly loadError = signal<boolean>(false);

  /** Sanitized blob URL safe for iframe binding. */
  readonly safeSrc = signal<SafeResourceUrl | null>(null);

  /** Raw blob URL for cleanup. */
  private blobUrl: string | null = null;

  constructor() {
    effect(() => {
      const f = this.file();
      this.revokeBlobUrl();
      this.safeSrc.set(null);

      if (!f) {
        this.isLoading.set(false);
        this.loadError.set(false);
        return;
      }

      this.fetchPdf(f);
    });

    this.destroyRef.onDestroy(() => this.revokeBlobUrl());
  }

  close(): void {
    this.opened.set(false);
  }

  retryLoad(): void {
    const f = this.file();
    if (f) this.fetchPdf(f);
  }

  @HostListener("window:keydown.escape")
  onOverlayKeydown(): void {
    this.close();
  }

  private resolveFileUrl(key: string): string {
    const defaultResolve = (k: string) => this.resourcePipe.transform(k, ...this.resourcePipeOptions());
    return this.resolveUrl()(key, defaultResolve);
  }

  private fetchPdf(key: string): void {
    this.isLoading.set(true);
    this.loadError.set(false);

    const url = this.resolveFileUrl(key);

    this.http.get(url, { responseType: "blob" }).subscribe({
      next: blob => {
        this.revokeBlobUrl();
        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        this.blobUrl = URL.createObjectURL(pdfBlob);
        this.safeSrc.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl));
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.loadError.set(true);
      },
    });
  }

  private revokeBlobUrl(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }
}
